import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../../../../contexts/AuthContext";
import {
  optimisationInputKey,
  runOptimisation,
} from "../domain/runOptimisation";
import { buildGroupReliefPdf, downloadForm } from "../importExport/pdfForms";
import {
  parseMainComputationSnapshot,
  resolveMainComputationSnapshot,
} from "../../../MainTaxComputation/utils/resolveMainComputationSnapshot";
import {
  createDraft,
  basisPeriod,
  hasYearAccess,
  initialCase,
  matchingComputations,
  MAX_COMPANIES,
  validateDraft,
  validateSetup,
} from "../domain/preparation";
import {
  buildExternalCompanyImport,
  externalCompanyInputToRow,
} from "../importExport/externalCompanyUpload";
import {
  createCorporateGroup as createCorporateGroupRequest,
  deleteCorporateGroup as deleteCorporateGroupRequest,
  getCorporateGroups,
  updateCorporateGroup as updateCorporateGroupRequest,
} from "../services/corporateGroup.service";

const asList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  throw new Error(
    "The company service returned an unexpected response. Please retry.",
  );
};
const message = (error, fallback) =>
  typeof error === "string" ? error : error?.message || fallback;

const useGroupReliefOptimiserWorkspace = () => {
  const auth = useContext(AuthContext);
  const services = useRef(auth);
  services.current = auth;
  const [settings, setSettings] = useState(initialCase);
  const [step, setStep] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [corporateGroups, setCorporateGroups] = useState([]);
  const [corporateGroupsLoading, setCorporateGroupsLoading] = useState(false);
  const [corporateGroupAction, setCorporateGroupAction] = useState("");
  const [corporateGroupError, setCorporateGroupError] = useState("");
  const [activeCorporateGroupId, setActiveCorporateGroupId] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [notice, setNotice] = useState("");
  const [reviewEligibilityConfirmed, setReviewEligibilityConfirmed] =
    useState(false);
  const [reviewFocusField, setReviewFocusField] = useState(null);
  const [companyChecksAttempted, setCompanyChecksAttempted] = useState(false);
  const [result, setResult] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [optimising, setOptimising] = useState(false);
  const [optimiseError, setOptimiseError] = useState("");
  const [exporting, setExporting] = useState(null);
  const [exportError, setExportError] = useState("");
  const liveInput = useRef(null);
  useEffect(() => {
    setReviewEligibilityConfirmed(false);
  }, [settings, selectedIds, drafts]);
  const generation = useRef(0);
  const catalogRequests = useRef({});
  const snapshotRequests = useRef({});
  const corporateGroupsLoaded = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current += 1;
    };
  }, []);

  const loadCompanies = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    setLoadError("");
    try {
      const list = asList(await services.current.getCompanyList("active"));
      if (!mounted.current || request !== generation.current) return;
      setCompanies((current) => [
        ...list,
        ...current.filter((company) => company.isExternal),
      ]);
      setCatalog((current) =>
        Object.fromEntries(
          Object.entries(current).filter(([, entry]) => entry.external),
        ),
      );
      // Bound concurrent requests: tax-computation lists also resolve exchange rates server-side.
      let cursor = 0;
      await Promise.all(
        Array.from({ length: Math.min(4, list.length) }, async () => {
          while (cursor < list.length) {
            if (!mounted.current || request !== generation.current) return;
            const company = list[cursor++];
            let entry;
            if (!hasYearAccess(company, settings.yearOfAssessment))
              entry = { computations: [], denied: true };
            else {
              try {
                entry = {
                  computations: asList(
                    await services.current.getTaxComputationList(company.c_id),
                  ),
                };
              } catch (error) {
                entry = {
                  computations: [],
                  error: message(
                    error,
                    "Unable to load computations. Retry the company list.",
                  ),
                };
              }
            }
            if (mounted.current && request === generation.current)
              setCatalog((current) => ({ ...current, [company.c_id]: entry }));
          }
        }),
      );
    } catch (error) {
      if (mounted.current && request === generation.current)
        setLoadError(message(error, "Unable to load companies. Please retry."));
    } finally {
      if (mounted.current && request === generation.current) setLoading(false);
    }
  }, [settings.yearOfAssessment]);

  const loadCorporateGroups = useCallback(async () => {
    setCorporateGroupsLoading(true);
    setCorporateGroupError("");
    try {
      const groups = await getCorporateGroups();
      if (!mounted.current) return;
      setCorporateGroups(Array.isArray(groups) ? groups : []);
      corporateGroupsLoaded.current = true;
    } catch (error) {
      if (mounted.current)
        setCorporateGroupError(
          message(error, "Unable to load saved corporate groups."),
        );
    } finally {
      if (mounted.current) setCorporateGroupsLoading(false);
    }
  }, []);

  const retryCompany = useCallback(
    async (company) => {
      const id = company?.c_id;
      if (!id || company.isExternal) return;

      const currentGeneration = generation.current;
      const request = (catalogRequests.current[id] || 0) + 1;
      catalogRequests.current[id] = request;
      setCatalog((current) => ({
        ...current,
        [id]: {
          ...current[id],
          error: "",
          loading: true,
        },
      }));

      let entry;
      if (!hasYearAccess(company, settings.yearOfAssessment)) {
        entry = { computations: [], denied: true };
      } else {
        try {
          entry = {
            computations: asList(
              await services.current.getTaxComputationList(id),
            ),
          };
        } catch (error) {
          entry = {
            computations: [],
            error: message(
              error,
              "Unable to load computations. Retry this company.",
            ),
          };
        }
      }

      if (
        mounted.current &&
        generation.current === currentGeneration &&
        catalogRequests.current[id] === request
      ) {
        setCatalog((current) => ({ ...current, [id]: entry }));
      }
    },
    [settings.yearOfAssessment],
  );

  const readSnapshot = async (id, tc) => {
    const request = (snapshotRequests.current[id] || 0) + 1;
    snapshotRequests.current[id] = request;
    try {
      const payload = await services.current.getMainCompDocOnly(tc.tc_id);
      const snapshot = parseMainComputationSnapshot(payload);
      resolveMainComputationSnapshot({
        computation: tc,
        doc: snapshot.doc,
        settings,
      });
      if (!mounted.current || snapshotRequests.current[id] !== request) return;
      setDrafts((current) => {
        const draft = current[id];
        if (!draft || draft.computation.tc_id !== tc.tc_id) return current;
        return {
          ...current,
          [id]: {
            ...draft,
            sourceDoc: snapshot.doc,
            sourceSavedAt: snapshot.savedAt,
            sourceNote:
              "Figures are based on the last saved Main Tax Computation. Please check the details carefully.",
            sourceStatus: "loaded",
          },
        };
      });
    } catch (error) {
      if (!mounted.current || snapshotRequests.current[id] !== request) return;
      setDrafts((current) => {
        const draft = current[id];
        if (!draft || draft.computation.tc_id !== tc.tc_id) return current;
        return {
          ...current,
          [id]: {
            ...draft,
            sourceStatus: "error",
            sourceDoc: null,
            sourceFiguresConfirmed: false,
            sourceNote: message(
              error,
              "Saved Main Tax Computation could not be loaded. Retry after checking that it has been saved.",
            ),
          },
        };
      });
    }
  };

  const chooseCompany = (company, tc, selected) => {
    if (!selected) {
      setSelectedIds((ids) => ids.filter((id) => id !== company.c_id));
      return;
    }
    if (
      !tc ||
      selectedIds.length >= MAX_COMPANIES ||
      !hasYearAccess(company, settings.yearOfAssessment)
    )
      return;
    if (
      selectedIds.some(
        (id) =>
          drafts[id]?.uen?.trim().toLowerCase() ===
          company.uen?.trim().toLowerCase(),
      )
    )
      return;
    setSelectedIds((ids) =>
      ids.includes(company.c_id) ? ids : [...ids, company.c_id],
    );
    if (drafts[company.c_id]?.computation.tc_id === tc.tc_id) return;
    setDrafts((current) => {
      const draft = createDraft(company, tc);
      return {
        ...current,
        [company.c_id]: company.isExternal
          ? {
              ...draft,
              isExternal: true,
              externalInput: company.externalInput,
              sourceDoc: company.externalSourceDoc,
              sourceSavedAt: null,
              sourceStatus: "loaded",
              sourceNote:
                "Figures were imported from Excel for this Group Relief case. Check them carefully before continuing.",
            }
          : draft,
      };
    });
    if (!company.isExternal) readSnapshot(company.c_id, tc);
  };
  const importExternalCompanies = (rows) => {
    const additions = [];
    const errors = [];
    const additionIndexesByUen = new Map();
    rows.forEach((row, index) => {
      try {
        const normalizedUen = String(row?.[1] || "")
          .trim()
          .toLowerCase();
        const existingCompany = companies.find(
          (company) =>
            company.isExternal &&
            company.uen?.trim().toLowerCase() === normalizedUen,
        );
        const existingComputation = existingCompany
          ? catalog[existingCompany.c_id]?.computations?.[0]
          : null;
        const imported = buildExternalCompanyImport(row, settings, {
          id: existingCompany?.c_id,
          computationId: existingComputation?.tc_id,
        });
        const existingIndex = additionIndexesByUen.get(normalizedUen);
        if (existingIndex === undefined) {
          additionIndexesByUen.set(normalizedUen, additions.length);
          additions.push({
            ...imported,
            replaced: Boolean(existingCompany),
          });
        } else {
          additions[existingIndex] = {
            ...imported,
            replaced: additions[existingIndex].replaced,
          };
        }
      } catch (error) {
        errors.push(
          `Row ${index + 2}: ${message(error, "Unable to import company.")}`,
        );
      }
    });
    if (additions.length) {
      setCompanies((current) => {
        const importedById = new Map(
          additions.map(({ company }) => [company.c_id, company]),
        );
        const currentIds = new Set(current.map((company) => company.c_id));
        return [
          ...current.map(
            (company) => importedById.get(company.c_id) || company,
          ),
          ...additions
            .map(({ company }) => company)
            .filter((company) => !currentIds.has(company.c_id)),
        ];
      });
      setCatalog((current) => ({
        ...current,
        ...Object.fromEntries(
          additions.map(({ company, computation }) => [
            company.c_id,
            { computations: [computation], external: true },
          ]),
        ),
      }));
      setDrafts((current) => {
        const next = { ...current };
        additions.forEach(({ company, computation }) => {
          const draft = next[company.c_id];
          if (!draft) return;
          next[company.c_id] = {
            ...draft,
            name: company.name,
            uen: company.uen,
            companyType: company.companyType,
            computation,
            externalInput: company.externalInput,
            sourceDoc: company.externalSourceDoc,
            externalPending: false,
            sourceStatus: "loaded",
            sourceSavedAt: null,
            sourceNote:
              "External details were replaced from Excel for this Group Relief case. Check the recalculated figures before continuing.",
            sourceFiguresConfirmed: false,
            confirmed: false,
          };
        });
        return next;
      });
    }
    return {
      added: additions.filter(({ replaced }) => !replaced).length,
      replaced: additions.filter(({ replaced }) => replaced).length,
      errors,
    };
  };
  const removeExternalCompany = (id) => {
    const company = companies.find(
      (item) => item.c_id === id && item.isExternal,
    );
    if (!company) return false;
    setCompanies((current) => current.filter((item) => item.c_id !== id));
    setCatalog((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
    setDrafts((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
    setSelectedIds((current) => current.filter((selectedId) => selectedId !== id));
    setActiveId((current) => (current === id ? null : current));
    return true;
  };
  const updateExternalCompany = (id, input) => {
    try {
      const existingCompany = companies.find((company) => company.c_id === id);
      const existingDraft = drafts[id];
      if (!existingCompany?.isExternal || !existingDraft?.isExternal)
        throw new Error("Only external companies can be edited here.");

      const imported = buildExternalCompanyImport(
        externalCompanyInputToRow(input),
        settings,
        {
          id,
          computationId: existingDraft.computation.tc_id,
        },
      );
      const normalizedUen = imported.company.uen.toLowerCase();
      const duplicateSelectedCompany = selectedIds.some(
        (selectedId) =>
          selectedId !== id &&
          drafts[selectedId]?.uen?.trim().toLowerCase() === normalizedUen,
      );
      if (duplicateSelectedCompany)
        throw new Error(
          "Another selected company already uses this UEN. Deselect it before saving this change.",
        );

      setCompanies((current) =>
        current.map((company) =>
          company.c_id === id ? imported.company : company,
        ),
      );
      setCatalog((current) => ({
        ...current,
        [id]: {
          computations: [imported.computation],
          external: true,
        },
      }));
      setDrafts((current) => {
        const draft = current[id];
        if (!draft) return current;
        return {
          ...current,
          [id]: {
            ...draft,
            name: imported.company.name,
            uen: imported.company.uen,
            companyType: imported.company.companyType,
            computation: imported.computation,
            externalInput: imported.company.externalInput,
            sourceDoc: imported.company.externalSourceDoc,
            sourceStatus: "loaded",
            sourceSavedAt: null,
            sourceNote:
              "External details were updated for this Group Relief case. Check the recalculated figures before continuing.",
            sourceFiguresConfirmed: false,
            confirmed: false,
          },
        };
      });
      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        error: message(error, "Unable to update the external company."),
      };
    }
  };
  const updateSettings = (patch) => {
    setSettings((current) => ({ ...current, ...patch }));
    setDrafts((current) =>
      Object.fromEntries(
        Object.entries(current).map(([id, draft]) => [
          id,
          { ...draft, confirmed: false, sourceFiguresConfirmed: false },
        ]),
      ),
    );
    if (selectedIds.length)
      setNotice(
        "Case settings changed. Check company selection and tick the figures check again for each company.",
      );
  };
  const selected = selectedIds.map((id) => drafts[id]).filter(Boolean);

  const corporateGroupPayload = (name) => ({
    name,
    companyIds: selected
      .filter((company) => !company.isExternal)
      .map((company) => company.id),
    externalMembers: selected
      .filter((company) => company.isExternal)
      .map((company) => ({ name: company.name, uen: company.uen })),
  });

  const saveCorporateGroup = async (name, groupId = "") => {
    setCorporateGroupAction("save");
    setCorporateGroupError("");
    try {
      const payload = corporateGroupPayload(name);
      const saved = groupId
        ? await updateCorporateGroupRequest(groupId, payload)
        : await createCorporateGroupRequest(payload);
      if (!mounted.current) return { ok: false };
      setCorporateGroups((current) => {
        const index = current.findIndex((group) => group.id === saved.id);
        if (index < 0) return [...current, saved];
        const next = [...current];
        next[index] = saved;
        return next;
      });
      setActiveCorporateGroupId(saved.id);
      return { ok: true, group: saved };
    } catch (error) {
      const errorMessage = message(
        error,
        "Unable to save the corporate group.",
      );
      if (mounted.current) setCorporateGroupError(errorMessage);
      return { ok: false, error: errorMessage };
    } finally {
      if (mounted.current) setCorporateGroupAction("");
    }
  };

  const removeCorporateGroup = async (groupId) => {
    setCorporateGroupAction("delete");
    setCorporateGroupError("");
    try {
      await deleteCorporateGroupRequest(groupId);
      if (!mounted.current) return { ok: false };
      setCorporateGroups((current) =>
        current.filter((group) => group.id !== groupId),
      );
      setActiveCorporateGroupId((current) =>
        current === groupId ? "" : current,
      );
      return { ok: true };
    } catch (error) {
      const errorMessage = message(
        error,
        "Unable to delete the corporate group.",
      );
      if (mounted.current) setCorporateGroupError(errorMessage);
      return { ok: false, error: errorMessage };
    } finally {
      if (mounted.current) setCorporateGroupAction("");
    }
  };

  const applyCorporateGroup = (groupId) => {
    const group = corporateGroups.find((item) => item.id === groupId);
    if (!group) return { ok: false, unavailable: ["Saved group not found."] };

    const nextIds = [];
    const nextDrafts = {};
    const unavailable = [];
    const usedUens = new Set();
    const occupiedCompanyIds = new Set(companies.map((company) => company.c_id));
    const pendingExternalCompanies = [];
    const pendingExternalCatalog = {};
    const basis = basisPeriod(settings.commonFinancialYearEnd);
    const pendingComputation = (id, uen) => ({
      tc_id: `external-pending-tc-${id}`,
      c_id: id,
      uen,
      companyType: "common",
      taxComputationType: "common",
      financialYearStart: basis?.start || "",
      financialYearEnd: basis?.end || "",
      yearOfAssessment: Number(settings.yearOfAssessment),
      currency: "SGD",
      currencyRate: 1,
      currency_rate: 1,
      taxPeriodType: "single_ya",
      status: "pending_external",
      isExternal: true,
    });
    const pendingDraft = (company) => ({
      ...createDraft(
        company,
        pendingComputation(company.c_id, company.uen),
      ),
      isExternal: true,
      externalPending: true,
      sourceStatus: "pending",
      sourceNote: `Upload the matching external company row for UEN ${company.uen}.`,
    });
    const addPendingExternalCompany = (member, label) => {
      const normalizedUen = String(member?.uen || "").trim().toLowerCase();
      if (!normalizedUen || usedUens.has(normalizedUen)) {
        unavailable.push(`${label} (missing or duplicate UEN)`);
        return;
      }
      const safeUen = normalizedUen.replace(/[^a-z0-9]+/gi, "-");
      let id = `external-pending-${safeUen || "company"}`;
      let suffix = 2;
      while (occupiedCompanyIds.has(id)) {
        id = `external-pending-${safeUen || "company"}-${suffix++}`;
      }
      const company = {
        c_id: id,
        name: String(member?.name || "External company").trim(),
        uen: String(member.uen).trim(),
        companyType: "common",
        isExternal: true,
        externalPending: true,
        externalInput: null,
        externalSourceDoc: null,
        permission: { role: "external", is_partial_access: false },
      };
      pendingExternalCompanies.push(company);
      pendingExternalCatalog[id] = {
        computations: [],
        external: true,
        pending: true,
      };
      occupiedCompanyIds.add(id);
      nextIds.push(id);
      nextDrafts[id] = pendingDraft(company);
      usedUens.add(normalizedUen);
    };
    const addCompany = (company, label) => {
      if (!company) {
        unavailable.push(label);
        return;
      }
      const normalizedUen = company.uen?.trim().toLowerCase();
      if (!normalizedUen || usedUens.has(normalizedUen)) {
        unavailable.push(`${label} (missing or duplicate UEN)`);
        return;
      }
      if (company.isExternal && company.externalPending) {
        nextIds.push(company.c_id);
        nextDrafts[company.c_id] =
          drafts[company.c_id] || pendingDraft(company);
        usedUens.add(normalizedUen);
        return;
      }
      if (!hasYearAccess(company, settings.yearOfAssessment)) {
        unavailable.push(`${label} (no access to this YA)`);
        return;
      }
      const matches = matchingComputations(
        catalog[company.c_id]?.computations || [],
        settings,
      );
      const existingDraft = drafts[company.c_id];
      const computation = matches.find(
        (item) => item.tc_id === existingDraft?.computation?.tc_id,
      );
      const chosen = computation || (matches.length === 1 ? matches[0] : null);
      if (!chosen) {
        unavailable.push(
          `${label} (${matches.length > 1 ? "choose a computation" : "no matching computation"})`,
        );
        return;
      }

      const draft =
        existingDraft?.computation?.tc_id === chosen.tc_id
          ? existingDraft
          : createDraft(company, chosen);
      nextIds.push(company.c_id);
      usedUens.add(normalizedUen);
      nextDrafts[company.c_id] = company.isExternal
        ? {
            ...draft,
            isExternal: true,
            externalInput: company.externalInput,
            sourceDoc: company.externalSourceDoc,
            sourceSavedAt: null,
            sourceStatus: "loaded",
            sourceNote:
              "Figures were imported from Excel for this Group Relief case. Check them carefully before continuing.",
          }
        : draft;
      if (!company.isExternal && draft !== existingDraft)
        readSnapshot(company.c_id, chosen);
    };

    (group.companyIds || []).forEach((companyId) => {
      const company = companies.find((item) => item.c_id === companyId);
      addCompany(company, company?.name || `Company ${companyId}`);
    });
    (group.externalMembers || []).forEach((member) => {
      const normalizedUen = member?.uen?.trim().toLowerCase();
      const company = companies.find(
        (item) =>
          item.isExternal && item.uen?.trim().toLowerCase() === normalizedUen,
      );
      if (company)
        addCompany(
          company,
          company.name ||
            `${member.name || "External company"} (upload required)`,
        );
      else
        addPendingExternalCompany(
          member,
          `${member.name || "External company"} (upload required)`,
        );
    });

    if (pendingExternalCompanies.length) {
      setCompanies((current) => [
        ...current,
        ...pendingExternalCompanies.filter(
          (company) =>
            !current.some((item) => item.c_id === company.c_id),
        ),
      ]);
      setCatalog((current) => ({
        ...current,
        ...pendingExternalCatalog,
      }));
    }
    setDrafts((current) => ({ ...current, ...nextDrafts }));
    setSelectedIds(nextIds);
    setActiveCorporateGroupId(group.id);
    return {
      ok: true,
      selectedCount: nextIds.length,
      unavailable,
      pendingExternalCount: pendingExternalCompanies.length,
    };
  };
  const updateDraft = (id, patch) =>
    setDrafts((current) => {
      const draft = { ...current[id], ...patch };
      // The figures checkbox is the only completion action. Eligibility must
      // still be valid, irrespective of the order in which fields are filled.
      draft.confirmed = !Object.keys(validateDraft(draft)).length;
      return { ...current, [id]: draft };
    });
  const goToCompanies = () => {
    if (Object.keys(validateSetup(settings)).length) return;
    setStep(1);
    loadCompanies();
    if (!corporateGroupsLoaded.current) loadCorporateGroups();
  };
  const selectionIssues = selectedIds.filter((id) => {
    const company = companies.find((item) => item.c_id === id);
    return (
      !company ||
      !hasYearAccess(company, settings.yearOfAssessment) ||
      !matchingComputations(catalog[id]?.computations || [], settings).some(
        (tc) => tc.tc_id === drafts[id]?.computation.tc_id,
      )
    );
  });
  const canContinue =
    !Object.keys(validateSetup(settings)).length &&
    selectedIds.length >= 2 &&
    selectedIds.length <= MAX_COMPANIES &&
    !loading &&
    !loadError &&
    !selectionIssues.length;
  const goToDetails = () => {
    if (!canContinue) return;
    setActiveId((current) =>
      selectedIds.includes(current) ? current : selectedIds[0],
    );
    setNotice("");
    setStep(2);
  };
  const nextCompany = () => {
    const index = selectedIds.indexOf(activeId);
    const ordered = [
      ...selectedIds.slice(index + 1),
      ...selectedIds.slice(0, index),
    ];
    const next = ordered.find((id) => !drafts[id]?.confirmed) || ordered[0];
    if (next) setActiveId(next);
  };
  const retrySnapshot = () => {
    const draft = drafts[activeId];
    updateDraft(activeId, {
      sourceStatus: "loading",
      sourceFiguresConfirmed: false,
    });
    readSnapshot(activeId, draft.computation);
  };
  const goToReview = () => {
    setCompanyChecksAttempted(true);
    for (const draft of selected) {
      const field = Object.keys(validateDraft(draft))[0];
      if (field || !draft.confirmed) {
        setActiveId(draft.id);
        setReviewFocusField(field || "sourceFiguresConfirmed");
        return;
      }
    }
    if (!canContinue) {
      setNotice(
        "Check the case settings and company selection before continuing.",
      );
      if (Object.keys(validateSetup(settings)).length) setStep(0);
      else goToCompanies();
      return;
    }
    setCompanyChecksAttempted(false);
    setReviewFocusField(null);
    setShowResults(false);
    setReviewOpen(true);
  };
  const inputKey = optimisationInputKey(
    selected,
    settings,
    reviewEligibilityConfirmed,
  );
  liveInput.current = inputKey;
  const resultStale = Boolean(
    result && (result.inputKey !== inputKey || !canContinue),
  );
  const optimise = async () => {
    if (optimising || !canContinue) return;
    setOptimising(true);
    setOptimiseError("");
    try {
      await new Promise((resolve) => setTimeout(resolve, 0));
      const next = runOptimisation(
        selected,
        settings,
        reviewEligibilityConfirmed,
      );
      if (!mounted.current || liveInput.current !== inputKey) return;
      setResult({ ...next, inputKey });
      setExportError("");
      setReviewOpen(false);
      setShowResults(true);
      setStep(3);
    } catch (error) {
      if (mounted.current)
        setOptimiseError(
          message(error, "Optimisation failed. Check the company details."),
        );
    } finally {
      if (mounted.current) setOptimising(false);
    }
  };
  const exportForm = async (type, declarations) => {
    if (!result || resultStale || exporting) return;
    setExporting(type);
    setExportError("");
    try {
      const bytes = await buildGroupReliefPdf(type, result, { declarations });
      if (!mounted.current) return;
      if (liveInput.current !== result.inputKey)
        throw new Error(
          "Inputs changed during export. Run optimisation again before downloading.",
        );
      downloadForm(bytes, type, result.settings.yearOfAssessment);
    } catch (error) {
      if (mounted.current)
        setExportError(
          message(error, "Unable to create the form. Please retry."),
        );
    } finally {
      if (mounted.current) setExporting(null);
    }
  };
  useEffect(() => {
    if (!selectedIds.length) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [selectedIds.length]);

  return {
    result,
    resultStale,
    showResults,
    reviewOpen,
    setReviewOpen,
    setShowResults,
    optimising,
    optimise,
    optimiseError,
    exportForm,
    exporting,
    exportError,
    reviewFocusField,
    companyChecksAttempted,
    goToReview,
    setReviewFocusField,
    reviewEligibilityConfirmed,
    setReviewEligibilityConfirmed,
    settings,
    updateSettings,
    step,
    setStep,
    companies,
    catalog,
    loading,
    loadError,
    loadCompanies,
    retryCompany,
    corporateGroups,
    corporateGroupsLoading,
    corporateGroupAction,
    corporateGroupError,
    activeCorporateGroupId,
    setActiveCorporateGroupId,
    loadCorporateGroups,
    saveCorporateGroup,
    removeCorporateGroup,
    applyCorporateGroup,
    selectedIds,
    selected,
    drafts,
    activeId,
    setActiveId,
    chooseCompany,
    importExternalCompanies,
    removeExternalCompany,
    updateExternalCompany,
    updateDraft,
    goToCompanies,
    goToDetails,
    canContinue,
    selectionIssues,
    nextCompany,
    retrySnapshot,
    notice,
    confirmedCount: selected.filter((company) => company.confirmed).length,
  };
};

export default useGroupReliefOptimiserWorkspace;
